/**
 * Whether any mounted page holds unsaved work (DV-3421 review round 5, item 5).
 *
 * <p>`useDirtyStateBlocker` guards in-app navigation with a router blocker,
 * which a document load does not pass through. Code that would reload the tab
 * by itself — `reconcileScope` after another tab re-anchors the shared token —
 * asks here first, and defers until the work is saved or discarded rather than
 * throw it away unasked.
 *
 * <p>A counter rather than a flag: several dirty pages (or one page mounted
 * twice in a test) each register, and each release only its own registration.
 * A leaf module with no store, so the reconciler can read it from a plain
 * listener.
 */

let dirtyCount = 0;
const clearedListeners = new Set<() => void>();

/** Mark one source of unsaved work. Returns its release; calling that twice is harmless. */
export const registerUnsavedWork = (): (() => void) => {
  dirtyCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    dirtyCount -= 1;
    if (dirtyCount === 0) clearedListeners.forEach((listener) => listener());
  };
};

export const hasUnsavedWork = (): boolean => dirtyCount > 0;

/** Call `listener` each time the last registration is released. Returns the unsubscribe. */
export const onUnsavedWorkCleared = (listener: () => void): (() => void) => {
  clearedListeners.add(listener);
  return () => {
    clearedListeners.delete(listener);
  };
};

/** Test seam. */
export const resetUnsavedWork = (): void => {
  dirtyCount = 0;
  clearedListeners.clear();
};
